// app/src/lib/cashflow/position.js — real-time cash position (US-3.1).
//
// Sources:
//   - plaidAccounts.currentBalanceCents (snapshot from last sync; tagged per type).
//   - plaidTransactions: today + last 7 days for the sparkline + pending preview.
//
// Concepts:
//   - "Cash" = sum of depository (checking + savings).
//   - "Credit utilization" reported separately; NOT added to cash.
//   - Pending transactions returned with estimatedClearedDate (date + 3 business days
//     heuristic) so the UI can flag them.

import { db } from '../db.js';
import { requireUserId } from '../procurement/util.js';
import { addDays, isoToday } from './util.js';
import { creditAccountIds, withoutCreditTxs, isCardPayment } from './accounts.js';
import { isOwnerDraw, getKeywords, get401kKeywords } from './ownerDraw.js';
import { is401kContribution } from './payroll.js';
import { isRevenueClawback } from '../revenue/depositRules.js';

const STALENESS_THRESHOLD_MS = 24 * 60 * 60 * 1000;
const PENDING_CLEAR_DAYS = 3;

// CSV/upload imports create a synthetic "manual" depository account (one under
// each importer) holding a one-time balance snapshot from the statement.
const MANUAL_ITEM_IDS = new Set(['upload', 'csv-import']);

/**
 * The depository accounts whose balances count toward cash. A manual CSV/upload
 * account is a placeholder snapshot; once the same account is coming from a live
 * Plaid feed, that snapshot is superseded — counting both double-counts cash. So
 * we drop the manual accounts whenever any live (non-manual) depository account
 * exists. With no live feed yet, the manual snapshot is all we have, so it still
 * counts. Pure + exported for testing.
 */
export function selectCashAccounts(accounts) {
  // Only accounts with an actual stored balance can contribute. Plaid accounts
  // don't currently persist a balance, so if the live feed has none we must fall
  // back to the manual (CSV) balance rather than showing $0.
  const depository = (accounts || []).filter((a) => a.type === 'depository' && a.currentBalanceCents != null);
  const live = depository.filter((a) => !MANUAL_ITEM_IDS.has(a.plaidItemId));
  return live.length > 0 ? live : depository;
}

/**
 * @returns {Promise<{
 *   asOf:number, stale:boolean, currency:string,
 *   cashCents:number, breakdown: Array<{ plaidAccountId:string, name:string, subtype:string, balanceCents:number }>,
 *   creditUtilization: { totalLimitCents:number, totalOwedCents:number, utilizationPct:number|null, accounts: Array<{ name:string, balanceCents:number, limitCents:number|null }> },
 *   pending: Array<{ plaidTxId:string, amountCents:number, merchantName:string, date:string, estimatedClearedDate:string }>,
 *   sparkline: Array<{ date:string, inflowCents:number, outflowCents:number, netCents:number }>,
 * }>}
 */
export async function getPosition() {
  const userId = await requireUserId();
  const accounts = await db.plaidAccounts.where('userId').equals(userId).toArray();
  const items = await db.plaidItems.where('userId').equals(userId).toArray();
  const asOf = items.length ? Math.max(...items.map((i) => i.lastSyncedAt || 0)) : 0;
  const stale = asOf === 0 || (Date.now() - asOf) > STALENESS_THRESHOLD_MS;

  const cashAccts = selectCashAccounts(accounts);
  const creditAccts = accounts.filter((a) => a.type === 'credit');

  const cashCents = cashAccts.reduce((s, a) => s + (a.currentBalanceCents || 0), 0);

  const totalLimit = creditAccts.reduce((s, a) => s + (a.limitCents || 0), 0);
  const totalOwed  = creditAccts.reduce((s, a) => s + Math.max(0, a.currentBalanceCents || 0), 0);

  // Cash flow / pending here means the DEPOSITORY (checking/savings) feed —
  // exclude credit-card charges so the sparkline + pending preview reflect the
  // bank, not card spending (which lives on the Spending tab).
  const creditSet = await creditAccountIds(userId);

  const today = isoToday();
  const sevenDaysAgo = addDays(today, -6);
  const recentTxs = withoutCreditTxs(await db.plaidTransactions
    .where('[userId+date]').between([userId, sevenDaysAgo], [userId, today], true, true)
    .toArray(), creditSet);

  const sparkline = buildSparkline(sevenDaysAgo, today, recentTxs);

  const pending = withoutCreditTxs(await db.plaidTransactions
    .where('userId').equals(userId)
    .filter((t) => t.pending)
    .toArray(), creditSet);

  const currency = accounts.find((a) => a.currency)?.currency || 'USD';

  return {
    asOf,
    stale,
    currency,
    cashCents,
    breakdown: cashAccts.map((a) => ({
      plaidAccountId: a.plaidAccountId,
      name: a.name,
      subtype: a.subtype || '',
      balanceCents: a.currentBalanceCents || 0,
    })),
    creditUtilization: {
      totalLimitCents: totalLimit,
      totalOwedCents: totalOwed,
      utilizationPct: totalLimit > 0 ? totalOwed / totalLimit : null,
      accounts: creditAccts.map((a) => ({
        name: a.name,
        balanceCents: a.currentBalanceCents || 0,
        limitCents: a.limitCents || null,
      })),
    },
    pending: pending.map((t) => ({
      plaidTxId: t.plaidTxId,
      amountCents: t.amountCents || 0,
      merchantName: t.merchantName || '',
      date: t.date,
      estimatedClearedDate: addDays(t.date, PENDING_CLEAR_DAYS),
    })),
    sparkline,
  };
}

// ── Minimum funding needs (runway gauge) ─────────────────────────────────────
// How many weeks of expenses does the bank balance cover? The expense rate is
// EVERYTHING the business spends — rent, payroll, suppliers, insurance, taxes —
// averaged over the last 12 weeks, under the app-wide charge-dated model:
// credit-card CHARGES are the spend (refunds net out) and the card BILL PAYMENT
// is skipped on both sides (a settlement transfer of spend already counted).
// Owner draws, 401(k) set-asides, and owner contributions are the owner's
// money, not expenses (same rule as every other cash view). Thresholds
// (owner-set 2026-07-08): danger below 1 week of expenses, minimum funding = 3.

export const FUNDING_WINDOW_WEEKS = 12;
export const FUNDING_MIN_WEEKS = 3;    // yellow below this many weeks of cover
export const FUNDING_DANGER_WEEKS = 1; // red below this

const daysBetweenIso = (aIso, bIso) =>
  Math.round((new Date(aIso + 'T00:00:00Z') - new Date(bIso + 'T00:00:00Z')) / 86400000);

/**
 * Pure core — classify the window's transactions into an expense rate and
 * grade the cash balance against it. Exported for tests.
 * @param {{ txs:Array, creditSet:Set<string>, drawKeywords:string[],
 *           k401Keywords:string[], cashCents:number, today:string }} a
 * @returns {{ expenseCents:number, weeklyExpenseCents:number, weeksObserved:number,
 *   minCents:number, dangerCents:number, weeksCover:number|null,
 *   level:'danger'|'warning'|'ok', cashCents:number }}
 */
export function computeFundingNeeds({ txs, creditSet, drawKeywords, k401Keywords, cashCents, today }) {
  let expenseCents = 0;
  let firstDate = null;
  for (const t of txs || []) {
    if (t.pending) continue;
    const amt = t.amountCents || 0;
    if (!firstDate || t.date < firstDate) firstDate = t.date;
    if (isCardPayment(t)) continue; // settlement transfer — the spend is the card charges
    if (creditSet && creditSet.has(t.plaidAccountId)) { expenseCents += amt; continue; } // charges + refunds, charge-dated
    if (amt <= 0) continue;                          // bank inflow
    if (isRevenueClawback(t)) continue;              // already netted into channel revenue
    if (is401kContribution(t, k401Keywords)) continue; // owner's retirement, not an expense
    if (isOwnerDraw(t, drawKeywords)) continue;        // profit to the owner, not an expense
    expenseCents += amt;
  }
  expenseCents = Math.max(0, expenseCents);
  // A short history must not dilute the rate: 2 weeks of data ÷ 12 would halve
  // the danger line. Scale by the weeks actually observed in the window.
  const observedDays = firstDate ? Math.min(FUNDING_WINDOW_WEEKS * 7, daysBetweenIso(today, firstDate) + 1) : 0;
  const weeksObserved = Math.max(1, observedDays / 7);
  const weeklyExpenseCents = Math.round(expenseCents / weeksObserved);
  const minCents = weeklyExpenseCents * FUNDING_MIN_WEEKS;
  const dangerCents = weeklyExpenseCents * FUNDING_DANGER_WEEKS;
  const cash = cashCents || 0;
  return {
    expenseCents, weeklyExpenseCents, weeksObserved, minCents, dangerCents,
    weeksCover: weeklyExpenseCents > 0 ? cash / weeklyExpenseCents : null,
    level: weeklyExpenseCents <= 0 ? 'ok' : cash < dangerCents ? 'danger' : cash < minCents ? 'warning' : 'ok',
    cashCents: cash,
  };
}

/**
 * Load the last 12 weeks of transactions and grade the cash position.
 * Pass `cashCents` (e.g. from getPosition()) to avoid recomputing it.
 */
export async function fundingNeeds({ cashCents, today = isoToday() } = {}) {
  const userId = await requireUserId();
  let cash = cashCents;
  if (cash == null) {
    const accounts = await db.plaidAccounts.where('userId').equals(userId).toArray();
    cash = selectCashAccounts(accounts).reduce((s, a) => s + (a.currentBalanceCents || 0), 0);
  }
  const from = addDays(today, -(FUNDING_WINDOW_WEEKS * 7 - 1));
  const txs = await db.plaidTransactions
    .where('[userId+date]').between([userId, from], [userId, today], true, true)
    .toArray();
  const creditSet = await creditAccountIds(userId);
  const drawKeywords = await getKeywords().catch(() => []);
  const k401Keywords = await get401kKeywords().catch(() => []);
  return computeFundingNeeds({ txs, creditSet, drawKeywords, k401Keywords, cashCents: cash, today });
}

function buildSparkline(startIso, endIso, txs) {
  const days = [];
  let cur = startIso;
  while (cur <= endIso) {
    days.push({ date: cur, inflowCents: 0, outflowCents: 0, netCents: 0 });
    cur = addDays(cur, 1);
  }
  const byDate = new Map(days.map((d) => [d.date, d]));
  for (const t of txs) {
    const bucket = byDate.get(t.date);
    if (!bucket) continue;
    if (t.pending) continue;
    // Plaid convention: positive amount = outflow (we already converted to cents
    // with positive=outflow in walMerger).
    if (t.amountCents > 0) bucket.outflowCents += t.amountCents;
    else bucket.inflowCents += -t.amountCents;
    bucket.netCents = bucket.inflowCents - bucket.outflowCents;
  }
  return days;
}
